/**
 * SmartInsights Rule Engine
 *
 * Pure-function rule engine for generating plain-English practice insights.
 * No Vue dependency — can be tested independently.
 *
 * Metrics shape:
 * {
 *   current:  { totalNet, totalClinicalHours, avgPerHour, dailyAvg, workingDays }
 *   prior:    { totalNet, totalClinicalHours, avgPerHour, dailyAvg, workingDays } | null
 *   providers: [{ name, totalNet, clinicalHours, perHour, workingDays }] | null
 *   dateRangeSpan: Number (days)
 *   activePreset: String | null
 *   currencySymbol: String
 * }
 */

// --- Helpers ---

function fmt(value, symbol = '$') {
  if (value == null || isNaN(value)) return 'N/A';
  return symbol + Math.round(value).toLocaleString('en-US');
}

function fmtDecimal(value, symbol = '$') {
  if (value == null || isNaN(value)) return 'N/A';
  return symbol + value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

function pct(value) {
  if (value == null || isNaN(value)) return '0';
  return Math.abs(value).toFixed(1);
}

function round1(value) {
  if (value == null || isNaN(value)) return '0';
  return value.toFixed(1);
}

function change(current, prior) {
  if (prior == null || prior === 0) return null;
  return ((current - prior) / Math.abs(prior)) * 100;
}

// --- Derived metrics (computed once, shared by all rules) ---

function computeDerived(metrics) {
  const { current, prior } = metrics;
  if (!current) return {};

  const productionPerDay = current.workingDays > 0 ? current.totalNet / current.workingDays : 0;
  const hoursPerDay = current.workingDays > 0 ? current.totalClinicalHours / current.workingDays : 0;

  const derived = {
    productionPerDay,
    hoursPerDay,
    priorProductionPerDay: null,
    priorHoursPerDay: null,
    netChange: null,
    perHourChange: null,
    hoursChange: null,
    daysChange: null,
    dailyAvgChange: null,
    hoursPerDayChange: null,
  };

  if (prior) {
    derived.priorProductionPerDay = prior.workingDays > 0 ? prior.totalNet / prior.workingDays : 0;
    derived.priorHoursPerDay = prior.workingDays > 0 ? prior.totalClinicalHours / prior.workingDays : 0;
    derived.netChange = change(current.totalNet, prior.totalNet);
    derived.perHourChange = change(current.avgPerHour, prior.avgPerHour);
    derived.hoursChange = change(current.totalClinicalHours, prior.totalClinicalHours);
    derived.daysChange = change(current.workingDays, prior.workingDays);
    derived.dailyAvgChange = change(current.dailyAvg, prior.dailyAvg);
    derived.hoursPerDayChange = change(hoursPerDay, derived.priorHoursPerDay);
  }

  return derived;
}

// --- Rules ---

const rules = [
  {
    id: 'doingMoreWithLess',
    category: 'efficiency',
    priority: 10,
    evaluate(metrics, derived) {
      if (!metrics.prior) return null;
      if (derived.netChange == null || derived.daysChange == null) return null;
      // Net production up AND working days down
      if (derived.netChange <= 2 || derived.daysChange >= -2) return null;

      const sym = metrics.currencySymbol || '$';
      return {
        type: 'positive',
        icon: 'la-trophy',
        headline: 'Doing More With Less',
        body: `Your practice produced ${pct(derived.netChange)}% more net revenue while working ${pct(Math.abs(derived.daysChange))}% fewer days. You generated ${fmt(derived.productionPerDay, sym)}/day compared to ${fmt(derived.priorProductionPerDay, sym)}/day previously — a sign of strong operational efficiency.`,
      };
    },
  },

  {
    id: 'workingSmarter',
    category: 'efficiency',
    priority: 9,
    evaluate(metrics, derived) {
      if (!metrics.prior) return null;
      if (derived.perHourChange == null || derived.hoursChange == null) return null;
      // $/hr is up AND total hours are flat or down
      if (derived.perHourChange <= 1) return null;
      if (derived.hoursChange > 3) return null;

      const sym = metrics.currencySymbol || '$';
      const hoursDesc = derived.hoursChange <= -1
        ? `while clinical hours decreased by ${pct(Math.abs(derived.hoursChange))}%`
        : 'while clinical hours stayed about the same';
      return {
        type: 'positive',
        icon: 'la-bolt',
        headline: 'Working Smarter',
        body: `Your average production per hour increased by ${pct(derived.perHourChange)}% to ${fmt(metrics.current.avgPerHour, sym)}/hr ${hoursDesc}. You're producing more value per chair-hour without adding hours.`,
      };
    },
  },

  {
    id: 'packingTheSchedule',
    category: 'efficiency',
    priority: 8,
    evaluate(metrics, derived) {
      if (!metrics.prior) return null;
      if (derived.hoursPerDayChange == null) return null;
      if (derived.hoursPerDayChange <= 3) return null;

      return {
        type: 'positive',
        icon: 'la-calendar-check',
        headline: 'Packing the Schedule',
        body: `You packed ${pct(derived.hoursPerDayChange)}% more clinical hours into each working day — averaging ${round1(derived.hoursPerDay)} hours/day vs ${round1(derived.priorHoursPerDay)} previously. Your schedule is being utilized more efficiently.`,
      };
    },
  },

  {
    id: 'revenuePerDay',
    category: 'growth',
    priority: 7,
    evaluate(metrics, derived) {
      if (!metrics.prior) return null;
      if (derived.dailyAvgChange == null) return null;
      if (Math.abs(derived.dailyAvgChange) < 5) return null;

      const sym = metrics.currencySymbol || '$';
      const diff = metrics.current.dailyAvg - metrics.prior.dailyAvg;

      if (derived.dailyAvgChange > 0) {
        return {
          type: 'positive',
          icon: 'la-chart-bar',
          headline: 'Revenue Per Day Is Growing',
          body: `Each working day generated ${fmt(metrics.current.dailyAvg, sym)} on average — up from ${fmt(metrics.prior.dailyAvg, sym)} last period. That's ${fmt(Math.abs(diff), sym)} more per day flowing into your practice.`,
        };
      } else {
        return {
          type: 'warning',
          icon: 'la-exclamation-circle',
          headline: 'Daily Revenue Has Declined',
          body: `Daily production averaged ${fmt(metrics.current.dailyAvg, sym)} — down from ${fmt(metrics.prior.dailyAvg, sym)} last period. Review scheduling density and case acceptance to identify what changed.`,
        };
      }
    },
  },

  {
    id: 'marginalDayValue',
    category: 'capacity',
    priority: 6,
    evaluate(metrics, derived) {
      if (derived.productionPerDay <= 0) return null;
      // Only show when we have enough data for it to be meaningful (>7 days)
      if (metrics.current.workingDays < 7) return null;

      const sym = metrics.currencySymbol || '$';
      const annualized = derived.productionPerDay * 12;

      return {
        type: 'opportunity',
        icon: 'la-lightbulb',
        headline: 'What One Extra Day Is Worth',
        body: `At your current rate of ${fmt(derived.productionPerDay, sym)}/day, each additional working day represents ~${fmt(derived.productionPerDay, sym)} in potential revenue. Adding just one clinical day per month could mean ~${fmt(annualized, sym)} annually.`,
      };
    },
  },

  {
    id: 'unrealizedCapacity',
    category: 'capacity',
    priority: 5,
    evaluate(metrics, derived) {
      if (!metrics.prior) return null;
      if (derived.daysChange == null || derived.daysChange >= 0) return null;
      if (derived.productionPerDay <= 0) return null;

      const sym = metrics.currencySymbol || '$';
      const daysDiff = Math.abs(metrics.current.workingDays - metrics.prior.workingDays);
      const missedRevenue = daysDiff * derived.productionPerDay;

      // Only meaningful if at least 3 fewer days
      if (daysDiff < 3) return null;

      return {
        type: 'opportunity',
        icon: 'la-expand-arrows-alt',
        headline: 'Unrealized Capacity',
        body: `You worked ${daysDiff} fewer days than last period. At your current daily rate of ${fmt(derived.productionPerDay, sym)}/day, maintaining the same number of working days would have produced an additional ~${fmt(missedRevenue, sym)}.`,
      };
    },
  },

  {
    id: 'fewerWorkingDays',
    category: 'neutral',
    priority: 4,
    evaluate(metrics, derived) {
      if (!metrics.prior) return null;
      if (derived.daysChange == null) return null;
      if (derived.daysChange >= -5) return null;
      // Skip if doingMoreWithLess would fire (net up AND days down)
      if (derived.netChange != null && derived.netChange > 2 && derived.daysChange < -2) return null;

      const daysDiff = Math.abs(metrics.current.workingDays - metrics.prior.workingDays);

      return {
        type: 'warning',
        icon: 'la-calendar-minus',
        headline: 'Fewer Working Days',
        body: `You worked ${metrics.current.workingDays} days compared to ${metrics.prior.workingDays} last period — ${daysDiff} fewer days (${pct(Math.abs(derived.daysChange))}% decrease). If intentional, this is great work-life balance. If not, check for cancellations or scheduling gaps.`,
      };
    },
  },

  {
    id: 'diminishingReturns',
    category: 'warning',
    priority: 9,
    evaluate(metrics, derived) {
      if (!metrics.prior) return null;
      if (derived.hoursChange == null || derived.perHourChange == null) return null;
      // Hours increased significantly but $/hr decreased
      if (derived.hoursChange <= 5 || derived.perHourChange >= 0) return null;

      return {
        type: 'warning',
        icon: 'la-exclamation-triangle',
        headline: 'Diminishing Returns on Hours',
        body: `Clinical hours increased by ${pct(derived.hoursChange)}% but hourly production dropped by ${pct(Math.abs(derived.perHourChange))}%. Adding more hours isn't translating to proportional revenue. Consider reviewing your case mix and fee schedule.`,
      };
    },
  },

  {
    id: 'providerGap',
    category: 'provider',
    priority: 7,
    evaluate(metrics) {
      if (!metrics.providers || metrics.providers.length < 2) return null;

      const withPerHour = metrics.providers.filter(p => p.perHour != null && p.perHour > 0);
      if (withPerHour.length < 2) return null;

      const sorted = [...withPerHour].sort((a, b) => b.perHour - a.perHour);
      const highest = sorted[0];
      const lowest = sorted[sorted.length - 1];
      const gap = ((highest.perHour - lowest.perHour) / lowest.perHour) * 100;

      if (gap < 30) return null;

      const sym = metrics.currencySymbol || '$';
      return {
        type: 'neutral',
        icon: 'la-users',
        headline: 'Provider Production Gap',
        body: `There's a ${Math.round(gap)}% gap between your highest-producing provider (${highest.name} at ${fmt(highest.perHour, sym)}/hr) and lowest (${lowest.name} at ${fmt(lowest.perHour, sym)}/hr). Consider reviewing case assignment and scheduling to balance the workload.`,
      };
    },
  },

  {
    id: 'hoursImbalance',
    category: 'provider',
    priority: 6,
    evaluate(metrics) {
      if (!metrics.providers || metrics.providers.length < 2) return null;

      const withHours = metrics.providers.filter(p => p.clinicalHours > 0);
      if (withHours.length < 2) return null;

      const sorted = [...withHours].sort((a, b) => b.clinicalHours - a.clinicalHours);
      const most = sorted[0];
      const least = sorted[sorted.length - 1];
      const ratio = most.clinicalHours / least.clinicalHours;

      if (ratio < 2) return null;

      const diffPct = ((most.clinicalHours - least.clinicalHours) / least.clinicalHours) * 100;
      return {
        type: 'neutral',
        icon: 'la-balance-scale',
        headline: 'Clinical Hours Imbalance',
        body: `${most.name} logged ${round1(most.clinicalHours)} clinical hours while ${least.name} logged ${round1(least.clinicalHours)} hours — a ${Math.round(diffPct)}% difference. This may indicate uneven scheduling or availability.`,
      };
    },
  },
];

// --- Evaluator ---

export function evaluateInsights(metrics, maxInsights = 5, customRules = []) {
  if (!metrics || !metrics.current) return [];

  const derived = computeDerived(metrics);
  const allRules = [...rules, ...customRules];

  const results = allRules
    .map(rule => {
      try {
        const insight = rule.evaluate(metrics, derived);
        if (!insight) return null;
        return { ...insight, id: rule.id, category: rule.category, priority: rule.priority };
      } catch (e) {
        console.warn(`SmartInsights: rule "${rule.id}" threw an error`, e);
        return null;
      }
    })
    .filter(Boolean)
    .sort((a, b) => b.priority - a.priority)
    .slice(0, maxInsights);

  return results;
}

export default rules;
